import { Dispatch } from 'redux';

import { createSlice } from 'common/modules/create-slice';
import { FeaturesState } from 'common/features/featuresReducer';

import { CategoryData } from 'common/api/categories/models/CategoryData';
import { EarnTypeConstructorFilter } from 'common/api/constructor/util';

export type Group = {
    group_id: string;
    display_name: string;
    count: number;
    data: object;
    // NOTE: We might not need to define these -- we don't care
    children: Array<any>;
    parents: Array<any>;
};

export type Category = {
    name: string;
    id: string;
    iconUrl: string;
    ecommId: number;
    earnType?: EarnTypeConstructorFilter;
    // NOTE: Extend type if needed
};

export const emptyCategory: Category = {
    name: '',
    id: '',
    iconUrl: '',
    ecommId: -1,
};

export type MetaData = {
    lastupdated: number;
};

export type ConstructorCategoryStateType = {
    giftCardCategories: Category[];
    shopOnlineCategories: Category[];
    combinedCategories: Category[];
    metadata: MetaData;
};

const ConstructorCategoryState: ConstructorCategoryStateType = {
    giftCardCategories: [],
    shopOnlineCategories: [],
    combinedCategories: [],
    metadata: { lastupdated: 0 },
};

const { reducer: categories, update } = createSlice(ConstructorCategoryState, 'DATA/CATEGORIES');

const getCategoriesThunk =
    (
        getCategories: () => Promise<
            { groups: Group[]; ecommCategories: CategoryData[] } | undefined
        >,
        earnType: EarnTypeConstructorFilter,
        force?: boolean
    ) =>
    async (dispatch: Dispatch, getState: () => FeaturesState) => {
        const data = getState().store.data.categories;
        let shouldRefetch = false;
        if (
            earnType === EarnTypeConstructorFilter.ShopOnline &&
            data.shopOnlineCategories.length === 0
        ) {
            shouldRefetch = true;
        } else if (
            earnType === EarnTypeConstructorFilter.GiftCards &&
            data.giftCardCategories.length === 0
        ) {
            shouldRefetch = true;
        }

        if (shouldRefetch || !!force) {
            const result = await getCategories();
            if (result && result.groups && result.ecommCategories) {
                const key =
                    earnType === EarnTypeConstructorFilter.GiftCards
                        ? 'giftCardCategories'
                        : 'shopOnlineCategories';
                dispatch(
                    update({
                        [key]: groupToCategory(result.groups, result.ecommCategories),
                    } as Pick<ConstructorCategoryStateType, 'giftCardCategories' | 'shopOnlineCategories'>)
                );
            }
        }
    };

// Resolves the categories for an earn type, fetching only when the store is
// empty, and returns the resulting list so callers do not depend on re-renders.
const ensureCategoriesByEarnTypeThunk =
    (
        getCategories: () => Promise<
            { groups: Group[]; ecommCategories: CategoryData[] } | undefined
        >,
        earnType?: EarnTypeConstructorFilter
    ) =>
    async (dispatch: Dispatch, getState: () => FeaturesState): Promise<Category[]> => {
        const existing = selectCategoriesByEarnType(getState(), earnType);
        if (existing.length > 0) {
            return existing;
        }

        const result = await getCategories();
        if (!result || !result.groups || !result.ecommCategories) {
            return [];
        }

        const categories = groupToCategory(result.groups, result.ecommCategories);
        const key =
            earnType === EarnTypeConstructorFilter.GiftCards
                ? 'giftCardCategories'
                : earnType === EarnTypeConstructorFilter.ShopOnline
                ? 'shopOnlineCategories'
                : 'combinedCategories';
        dispatch(
            update({ [key]: categories } as Pick<
                ConstructorCategoryStateType,
                'giftCardCategories' | 'shopOnlineCategories' | 'combinedCategories'
            >)
        );
        return categories;
    };

const noCategories: Category[] = [];

export const selectCategoriesByEarnType = (
    s: FeaturesState,
    earnType?: EarnTypeConstructorFilter
) => {
    if (earnType === EarnTypeConstructorFilter.GiftCards) {
        return s.store.data.categories.giftCardCategories;
    }
    if (earnType === EarnTypeConstructorFilter.ShopOnline) {
        return s.store.data.categories.shopOnlineCategories;
    }
    // Guard against rehydrated state persisted before this key existed.
    return s.store.data.categories.combinedCategories || noCategories;
};

// util functions
const groupToCategory = (groups: Group[], eCommCategories: CategoryData[]): Category[] => {
    const categoryMap = eCommCategories.reduce((acc, category) => {
        const { name } = category;

        return { ...acc, [name]: category };
    }, {} as Record<string, CategoryData>);

    return groups.map((group: Group) => ({
        name: group.display_name,
        id: group.group_id,
        iconUrl: categoryMap[group.display_name]?.iconUrl,
        ecommId: categoryMap[group.display_name]?.id,
    }));
};

export { categories, getCategoriesThunk, ensureCategoriesByEarnTypeThunk };
